import { RefObject, useEffect, useState } from "react";

/**
 * Tracks an element's on-screen height via ResizeObserver (plus a window
 * resize listener for viewport-driven changes the observer can miss).
 *
 * Uses getBoundingClientRect().height — this matches what's actually on
 * screen (clientHeight can report slightly different values depending on
 * scrollbar / box-sizing quirks). Returns 0 until the element mounts.
 *
 * Pass enabled=false to skip observing (e.g. when a parent already measures
 * the same element and provides the value).
 */
export const useElementHeight = (
  ref: RefObject<HTMLElement | null>,
  enabled = true
): number => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    // Read ref.current fresh on each measure rather than the `el` captured
    // above, so the window-resize path measures the live element even if it
    // was swapped without re-running this effect.
    const update = () => {
      const current = ref.current;
      if (current) setHeight(current.getBoundingClientRect().height);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [ref, enabled]);

  return height;
};
